import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  getMode,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch
} from '@stencil/core';
import { Labels, translate } from '../../../../utils/translations';
import { emitCustomEvent, jsonWatcher, registerClickOutside } from '../../../../utils/utils';

const minRealTimeSearchLength = 2;

@Component({
  tag: 'ds-search-input',
  styleUrls: {
    default: 'ds-search-input.scss',
    light: 'ds-search-input.scss'
  },
  shadow: true
})
export class SearchInput implements ComponentInterface {
  private _searchFormElement!: HTMLFormElement;
  private _searchInputElement!: HTMLInputElement;

  @Prop() url?: string = '';
  @Prop() inputName?: string = 'q';
  @Prop() placeholder?: string = translate(Labels.Search);
  @Prop() allowEmptySearchSubmit?: boolean = false;
  @Prop() hideSubmitButton?: boolean = false;
  @Prop() autoFocus?: boolean = false;
  @Prop() realTimeSearch?: boolean = false;
  @Prop() type?: 'masthead' | 'menu' = 'masthead';
  @Prop() queryParameters?: { [key: string]: string | boolean } | string;
  private _queryParameters: { [key: string]: string | boolean };

  @Event() searchInputClosed: EventEmitter;
  @Event() searchInputCleared: EventEmitter;
  @Event() searchQueryChanged: EventEmitter;
  @Event() searchQuerySubmitted: EventEmitter;
  @Event() cxsFlyoutOpen: EventEmitter;
  @Event() cxsFlyoutClose: EventEmitter;

  @State() searchQuery: string = '';

  @Element() el: HTMLDsSearchInputElement;

  @Watch('queryParameters')
  private _updateProps(): void {
    this._propWatcher('queryParameters', this.queryParameters);
  }

  @Watch('searchQuery')
  handleSearchFlyout(newValue: string, previousValue: string): void {
    this.searchQueryChanged.emit(newValue);
    if (!this.realTimeSearch) {
      return;
    }
    const wasBelowThreshold = previousValue.length < minRealTimeSearchLength;
    const isAboveThreshold = newValue.length >= minRealTimeSearchLength;
    if (wasBelowThreshold && isAboveThreshold) {
      this.cxsFlyoutOpen.emit();
    } else if (!wasBelowThreshold && !isAboveThreshold) {
      this.cxsFlyoutClose.emit();
    }
  }

  @Method()
  async setValue(value: string) {
    this.searchQuery = value;
  }

  componentWillLoad(): void {
    this._updateProps();
  }

  disconnectedCallback() {
    // when element is removed by conditional rendering, this.event.emit() is not fired, probably
    // because at this time stencil context is already destroyed,
    this.cxsFlyoutClose.emit();
    emitCustomEvent('searchQueryChanged', '');
  }

  componentDidLoad(): void {
    if (this.autoFocus) {
      this._searchInputElement.focus();
    }
    registerClickOutside(this, this.el, () => {
      if (
        this.realTimeSearch &&
        this.searchQuery.length >= minRealTimeSearchLength &&
        this.type === 'masthead'
      ) {
        this.searchQuery = '';
      }
    });
  }

  private handleChange(e) {
    this.searchQuery = (e.target as HTMLInputElement).value;
  }

  private handleKeyDown(e) {
    switch (e.code) {
      case 'Escape':
        if (this.searchQuery.length) {
          this.searchQuery = '';
        } else {
          this.searchInputClosed.emit();
        }
        return;
      case 'Enter':
        if (
          (!(this.searchQuery.length > 0) && !this.allowEmptySearchSubmit) ||
          this.hideSubmitButton
        ) {
          e.preventDefault();
        } else {
          this.searchQuerySubmitted.emit(this.searchQuery);
        }
        return;
      default:
        break;
    }
  }

  private handleSubmit() {
    this.searchQuerySubmitted.emit(this.searchQuery);
    this._searchFormElement.submit();
  }

  private handleClean() {
    this.searchInputCleared.emit(this.searchQuery);
    if (this.searchQuery.length) {
      this.searchQuery = '';
    }
    this._searchInputElement.focus();
  }

  private _propWatcher(
    prop: string,
    value: { [key: string]: string | boolean } | string | boolean
  ): void {
    const resolved = jsonWatcher(prop, value);
    if (resolved) {
      this[`_${resolved.prop}`] = resolved.value;
    }
  }

  render() {
    const isRTL = window?.getComputedStyle(this.el).getPropertyValue('direction') === 'rtl';
    return (
      <Host class={`search--${this.type}`}>
        <form
          ref={(el) => (this._searchFormElement = el as HTMLFormElement)}
          action={this.url}
          data-mode={getMode(this)}
        >
          <div
            class={{
              'search-input-wrapper': true,
              'search-input-wrapper--with-submit': !this.hideSubmitButton,
              'search-input-wrapper--with-text': this.searchQuery.length > 0
            }}
          >
            {this._queryParameters &&
              Object.keys(this._queryParameters).map((parameter, index) => (
                <input
                  type='hidden'
                  name={parameter}
                  value={this._queryParameters[parameter].toString()}
                  key={index}
                />
              ))}
            <input
              ref={(el) => (this._searchInputElement = el as HTMLInputElement)}
              class={{ 'search-input': true, rtl: isRTL }}
              type='search'
              autoComplete='off'
              placeholder={this.placeholder}
              name={this.inputName}
              value={this.searchQuery}
              onInput={(event) => this.handleChange(event)}
              onKeyDown={this.handleKeyDown.bind(this)}
              data-testid='cxs-search-input'
            />
            {this.searchQuery.length > 0 && (
              <ds-navigation-button
                class='search-button search-button--clean'
                color='icon'
                type='cancel'
                onClick={() => this.handleClean()}
                label={translate(Labels['Search.Cancel'])}
                title={translate(Labels['Search.Cancel'])}
                data-testid='cxs-search-input-clear-button'
              >
                <ds-icon name='Decline' class='search-icon' />
              </ds-navigation-button>
            )}
            {(this.searchQuery.length > 0 || this.allowEmptySearchSubmit) &&
            !this.hideSubmitButton ? (
              <ds-navigation-button
                class='search-button search-button--submit'
                color='icon'
                type='submit'
                onClick={() => this.handleSubmit()}
                label={translate(Labels['Search.Submit'])}
                title={translate(Labels['Search.Submit'])}
                data-testid='cxs-search-input-submit-button'
              >
                <ds-icon name='Search' class='search-icon' />
              </ds-navigation-button>
            ) : null}
          </div>
        </form>
        {this.realTimeSearch && this.searchQuery.length >= minRealTimeSearchLength && (
          <ds-search-fly-out type={this.type}>
            <slot name='search-title' slot='search-title'></slot>
            <slot name='search-results' slot='search-results'></slot>
          </ds-search-fly-out>
        )}
      </Host>
    );
  }
}
